<script lang="ts">
export default {
  name: 'WholesalerGroupUpdateModal',
};
</script>

<script setup lang="ts">
import { ref, computed } from 'vue';
import ConfirmModal from './ConfirmModal.vue';
import Combobox from '@/components/v2/Combobox.vue';
import type { ComboboxOption } from '@/components/v2/Combobox.vue';
import TextField from '@/components/v2/TextField.vue';
import Toggle from '@/components/v2/Toggle.vue';
import { WholesalerGroup } from '@/types/WholesalerGroup';
import { WholesalerAdmin } from '@/types/WholesalerAdmin';
import AppButton from '@/components/v2/AppButton.vue';
import { Edit2 } from 'lucide-vue-next';

const props = withDefaults(
  defineProps<{
    wholesalerGroup: WholesalerGroup;
    wholesalerGroups: WholesalerGroup[];
    wholesalers: WholesalerAdmin[];
    /** When true, modal is controlled by parent (open on mount, no trigger button). */
    show?: boolean;
  }>(),
  { show: undefined },
);

const emit = defineEmits<{
  change: [WholesalerGroup, boolean];
  closed: [];
}>();

const wholesalerGroupTypes = ['Co-Broker', 'MGA', 'MGU', 'Wholesaler'];
const typeOptions: ComboboxOption[] = wholesalerGroupTypes.map((t) => ({ label: t, value: t }));

const selectedWholesalerGroup = ref<string>('');
const wholesalerGroupType = ref<string>('');
const deleteFlagged = ref<boolean>(false);
const error = ref<string>('');

const groupCount = computed(() => {
  return props.wholesalers.reduce((count, g) => (g.wholesalerGroup === props.wholesalerGroup.name ? count + 1 : count), 0);
});

async function renameWholesalerGroup() {
  if (!deleteFlagged.value && selectedWholesalerGroup.value.trim() !== props.wholesalerGroup.name) {
    if (props.wholesalerGroups.map((g) => g.name).includes(selectedWholesalerGroup.value)) {
      error.value = 'Wholesaler Group name already exists';
      return false;
    } else if (selectedWholesalerGroup.value.trim() === '') {
      error.value = 'Wholesaler Group name cannot be empty';
      return false;
    }
  }

  const group = JSON.parse(JSON.stringify(props.wholesalerGroup)) as WholesalerGroup;
  group.name = selectedWholesalerGroup.value.trim();
  group.type = wholesalerGroupType.value;

  emit('change', group, deleteFlagged.value);

  selectedWholesalerGroup.value = '';
  error.value = '';
  return true;
}

function onDismiss() {
  selectedWholesalerGroup.value = '';
  error.value = '';
}

function dialogOpened() {
  selectedWholesalerGroup.value = props.wholesalerGroup.name;
  wholesalerGroupType.value = props.wholesalerGroup?.type ?? '';
}
</script>

<template>
  <ConfirmModal
    title="Editing Wholesaler Group"
    :subtitle="wholesalerGroup.name"
    :action="renameWholesalerGroup"
    confirmText="Save"
    :dismiss="onDismiss"
    size="md"
    :show="show"
    @open="dialogOpened"
    @closed="emit('closed')">
    <template
      v-if="show !== true"
      #button="{ props: slotProps }">
      <AppButton
        class="tw:rounded-full"
        v-bind="slotProps">
        <Edit2 :size="16" />
      </AppButton>
    </template>

    <div class="tw:mt-2 tw:flex tw:flex-col tw:gap-4 tw:p-2">
      <TextField
        v-model="selectedWholesalerGroup"
        label="Name"
        data-test="rename-wholesaler-text" />
      <div>
        <label
          for="wholesaler-group-type"
          class="tw:mb-1 tw:block tw:text-sm tw:font-medium tw:text-gray-700">
          Type
        </label>
        <Combobox
          id="wholesaler-group-type"
          v-model="wholesalerGroupType"
          :options="typeOptions"
          placeholder="Select type"
          data-test="wholesaler-group-type" />
      </div>
      <Toggle
        v-model="deleteFlagged"
        label="Delete"
        :disabled="groupCount > 0" />
      <p
        v-if="groupCount > 0"
        class="tw:text-sm tw:text-gray-600">
        To enable wholesaler group deletion, first unlink from {{ groupCount }} wholesaler{{ groupCount > 1 ? 's' : '' }}
      </p>
      <p
        v-if="error"
        class="tw:text-sm tw:text-error">
        {{ error }}
      </p>
    </div>
  </ConfirmModal>
</template>
